import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Shield, AlertTriangle } from "lucide-react";

interface EditRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  member: any;
  teamName: string;
}

export function EditRoleModal({
  isOpen,
  onClose,
  onSave,
  member,
  teamName
}: EditRoleModalProps) {
  const [teamRole, setTeamRole] = useState(member?.role || member?.teamRole || 'MEMBER');
  const [newRole, setNewRole] = useState(member?.user?.jobTitle || '');
  const [department, setDepartment] = useState(member?.user?.department || '');
  const [reason, setReason] = useState('');
  const [notifyMember, setNotifyMember] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const teamRoles = [
    { value: 'MEMBER', label: 'Member', description: 'Standard team member with basic access' },
    { value: 'TEAM_LEAD', label: 'Team Lead', description: 'Team lead with additional responsibilities' },
    { value: 'MANAGER', label: 'Manager', description: 'Team manager with full team management access' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        teamRole,
        newRole: newRole.trim() || undefined,
        department: department.trim() || undefined,
        reason: reason.trim(),
        notifyMember
      });
      onClose();
      setReason('');
    } catch (error) {
      console.error('Failed to update role:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setTeamRole(member?.role || member?.teamRole || 'MEMBER');
      setNewRole(member?.user?.jobTitle || '');
      setDepartment(member?.user?.department || '');
      setReason('');
      setNotifyMember(true);
    }
  };

  if (!member) return null;

  const currentTeamRole = member.role || member.teamRole || 'MEMBER';
  const roleChanged = teamRole !== currentTeamRole;
  const profileChanged = newRole.trim() !== (member.user?.jobTitle || '') ||
                        department.trim() !== (member.user?.department || '');

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Edit Member Role
          </DialogTitle>
          <DialogDescription>
            Update the team role and profile information for {member.user?.fullName || `${member.user?.firstName} ${member.user?.lastName}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-6">
            {/* Current Role Info */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Current Information</p>
                  <p className="text-sm text-muted-foreground">Team: {teamName}</p>
                </div>
                  <div className="text-right space-y-1">
                    <div>
                      <Badge variant={currentTeamRole === 'MANAGER' ? 'default' : 'secondary'}>
                        {currentTeamRole === 'MANAGER' ? 'Manager' :
                         currentTeamRole === 'TEAM_LEAD' ? 'Team Lead' : 'Member'}
                      </Badge>
                  </div>
                  {member.user?.jobTitle && (
                    <p className="text-sm text-muted-foreground">{member.user.jobTitle}</p>
                  )}
                  {member.user?.department && (
                    <p className="text-xs text-muted-foreground">{member.user.department}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Team Role Selection */}
            <div className="space-y-3">
              <Label htmlFor="team-role">Team Role</Label>
              <Select value={teamRole} onValueChange={setTeamRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team role" />
                </SelectTrigger>
                <SelectContent>
                  {teamRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{role.label}</span>
                        <span className="text-xs text-muted-foreground">{role.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Job Title */}
            <div className="space-y-2">
              <Label htmlFor="job-title">Job Title (Optional)</Label>
              <Input
                id="job-title"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                placeholder="e.g., Senior Developer, Product Manager"
              />
            </div>

            {/* Department */}
            <div className="space-y-2">
              <Label htmlFor="department">Department (Optional)</Label>
              <Input
                id="department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g., Engineering, Marketing"
              />
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Change</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for this role change..."
                required
                rows={3}
              />
            </div>

            {/* Notification Option */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="notify"
                checked={notifyMember}
                onCheckedChange={(checked) => setNotifyMember(checked === true)}
              />
              <Label htmlFor="notify" className="text-sm">
                Notify member via email about this role change
              </Label>
            </div>

            {/* Change Summary */}
            {(roleChanged || profileChanged) && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Changes Summary</p>
                  <ul className="text-blue-700 space-y-1 mt-1">
                    {roleChanged && (
                      <li>• Team role: {currentTeamRole} → {teamRole}</li>
                    )}
                    {newRole.trim() !== (member.user?.jobTitle || '') && (
                      <li>• Job title: {member.user?.jobTitle || 'None'} → {newRole.trim() || 'None'}</li>
                    )}
                    {department.trim() !== (member.user?.department || '') && (
                      <li>• Department: {member.user?.department || 'None'} → {department.trim() || 'None'}</li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!reason.trim() || isSubmitting || (!roleChanged && !profileChanged)}
            >
              {isSubmitting ? 'Updating...' : 'Update Role'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
